import React from 'react';
import { createRoutesFromChildren, matchRoutes, useNavigationType } from 'react-router';

import * as Sentry from '@sentry/react';
import { isProductionEnvironment } from '@treneo/environments';
import { logger } from '@treneo/shared/logger';
import { useAppLocation } from '@treneo/shared/router';

type SentrySettings = {
  dsn?: string;
  sentryEnvironment?: string;
};

type SentryInitializationOptions = {
  sentrySettings: SentrySettings;
};

export const initializeSentry = ({ sentrySettings }: SentryInitializationOptions): void => {
  if (!isProductionEnvironment()) {
    logger.log('Sentry disabled in development environment');
    return;
  }

  if (!sentrySettings?.dsn || !sentrySettings?.sentryEnvironment) {
    logger.log('Sentry configuration missing, skipping initialization');
    return;
  }

  try {
    Sentry.init({
      dsn: sentrySettings.dsn,
      environment: sentrySettings.sentryEnvironment,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation: useAppLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        }),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true
        }),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
        Sentry.httpClientIntegration(),
        Sentry.extraErrorDataIntegration()
      ],
      tracesSampleRate: 0.25,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1,
      beforeSend(event) {
        const msg = event.message ?? event.exception?.values?.[0]?.value ?? '';
        if (msg.includes('Server timeout elapsed without receiving a message from the server')) {
          return null;
        }

        return event;
      }
    });

    logger.log('Sentry initialized successfully');
  } catch (error) {
    logger.error('Failed to initialize Sentry:', error);
  }
};
